import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { SearchIcon, ShieldCheckIcon, SparklesIcon, PaperclipIcon, SendIcon, MoreVerticalIcon, CheckIcon, CheckCheckIcon, ChevronLeftIcon, ChevronDownIcon, PhoneIcon, MicIcon, ShieldAlertIcon } from 'lucide-react';
import { ListingDetailModal } from '../components/ListingDetailModal';
import { ListingCardProps } from '../components/ListingCard';
type Presence = {
  state: 'online' | 'typing' | 'offline';
  lastSeen?: string;
};
type MsgStatus = 'sent' | 'delivered' | 'read';
interface Message {
  id: number;
  fromMe: boolean;
  text: string;
  time: string;
  status?: MsgStatus;
  kind?: 'safety';
}
interface Conversation {
  id: string;
  name: string;
  type: string;
  lastMessage: string;
  time: string;
  unread: number;
  avatar: string;
  listing: string | null;
  listingImage?: string;
  listingPrice?: string;
  presence: Presence;
}
const mockConversations: Conversation[] = [{
  id: '1',
  name: 'Al Futtaim Automall',
  type: 'Verified Business',
  lastMessage: 'Yes, the car is available for viewing today.',
  time: '10:42 AM',
  unread: 2,
  avatar: 'A',
  listing: 'Toyota Prado GXR 2018',
  listingImage: 'https://images.unsplash.com/photo-1590362891991-f776e747a588?auto=format&fit=crop&q=80&w=400',
  listingPrice: 'AED 82,000',
  presence: {
    state: 'online'
  }
}, {
  id: '2',
  name: 'Talab AI Assistant',
  type: 'AI Agent',
  lastMessage: "I've found 3 new matches for your villa request.",
  time: 'Yesterday',
  unread: 0,
  avatar: 'T',
  listing: null,
  presence: {
    state: 'online'
  }
}, {
  id: '3',
  name: 'Ahmed M.',
  type: 'Individual',
  lastMessage: 'Can we negotiate the price?',
  time: 'Mon',
  unread: 0,
  avatar: 'AM',
  listing: 'MacBook Pro M2',
  listingImage: 'https://images.unsplash.com/photo-1517336714731-489689fd1ca8?auto=format&fit=crop&q=80&w=400',
  listingPrice: 'AED 4,200',
  presence: {
    state: 'offline',
    lastSeen: 'today at 9:14 AM'
  }
}];
const initialThreads: Record<string, Message[]> = {
  '1': [{
    id: 1,
    fromMe: true,
    text: 'Hi, is the car still available?',
    time: '10:38 AM',
    status: 'read'
  }, {
    id: 2,
    fromMe: false,
    text: 'Yes, the car is available for viewing today. We are open until 8 PM.',
    time: '10:40 AM'
  }, {
    id: 3,
    fromMe: true,
    text: 'Great! Can I come by around 5 PM?',
    time: '10:41 AM',
    status: 'read'
  }, {
    id: 4,
    fromMe: false,
    text: 'Yes, the car is available for viewing today.',
    time: '10:42 AM'
  }],
  '2': [{
    id: 1,
    fromMe: false,
    text: "I've found 3 new matches for your villa request.",
    time: 'Yesterday'
  }],
  '3': [{
    id: 1,
    fromMe: false,
    text: 'Can we negotiate the price?',
    time: 'Mon'
  }, {
    id: 2,
    fromMe: true,
    text: "Let's discuss — what did you have in mind?",
    time: 'Mon',
    status: 'delivered'
  }]
};
function presenceLabel(p: Presence) {
  if (p.state === 'online') return {
    text: 'online',
    cls: 'text-green-600'
  };
  if (p.state === 'typing') return {
    text: 'typing…',
    cls: 'text-primary-indigo'
  };
  return {
    text: `last seen ${p.lastSeen ?? 'recently'}`,
    cls: 'text-slate-400'
  };
}
function Ticks({
  status
}: {
  status?: MsgStatus;
}) {
  if (!status) return null;
  if (status === 'sent') return <CheckIcon className="w-3.5 h-3.5 text-white/60" data-id="element-2685" />;
  if (status === 'delivered') return <CheckCheckIcon className="w-3.5 h-3.5 text-white/60" data-id="element-2686" />;
  return <CheckCheckIcon className="w-3.5 h-3.5 text-sky-300" data-id="element-2687" />;
}
function Avatar({
  chat,
  size = 'md'
}: {
  chat: Conversation;
  size?: 'md' | 'sm';
}) {
  const dims = size === 'sm' ? 'w-10 h-10' : 'w-12 h-12';
  const icon = size === 'sm' ? 'w-5 h-5' : 'w-6 h-6';
  return <div className="relative flex-shrink-0" data-id="element-2688">
      <div className={`${dims} rounded-full flex items-center justify-center text-white font-bold ${chat.type === 'AI Agent' ? 'bg-gradient-to-br from-primary-indigo to-ai-purple' : 'bg-slate-300'}`} data-id="element-2689">
        {chat.type === 'AI Agent' ? <SparklesIcon className={icon} data-id="element-2690" /> : chat.avatar}
      </div>
      {chat.presence.state === 'online' && <span className="absolute bottom-0 right-0 w-3.5 h-3.5 bg-green-500 border-2 border-white rounded-full" data-id="element-2691" />}
    </div>;
}
export function Messages() {
  const [activeChat, setActiveChat] = useState<Conversation | null>(null);
  const [threads, setThreads] = useState<Record<string, Message[]>>(initialThreads);
  const [draft, setDraft] = useState('');
  const [itemExpanded, setItemExpanded] = useState(true);
  const [showItemDetail, setShowItemDetail] = useState(false);
  const listingDetail: ListingCardProps | null = activeChat && activeChat.listing ? {
    id: `chat-${activeChat.id}`,
    title: activeChat.listing,
    price: activeChat.listingPrice ?? 'Price on request',
    location: 'Dubai, UAE',
    images: [activeChat.listingImage ?? 'https://images.unsplash.com/photo-1590362891991-f776e747a588?auto=format&fit=crop&q=80&w=800'],
    specs: [{
      label: 'Condition',
      value: 'Excellent'
    }, {
      label: 'Seller',
      value: activeChat.name
    }],
    matchScore: 96,
    sellerType: activeChat.type === 'Verified Business' ? 'Verified Business' : 'Individual'
  } : null;
  const scrollRef = useRef<HTMLDivElement>(null);
  const prevCountRef = useRef(0);
  const messages = activeChat ? threads[activeChat.id] ?? [] : [];
  const presence = activeChat ? presenceLabel(activeChat.presence) : null;
  // On opening a chat: expand the item and reset the message counter.
  useEffect(() => {
    if (!activeChat) return;
    setItemExpanded(true);
    prevCountRef.current = threads[activeChat.id]?.length ?? 0;
  }, [activeChat]);
  // As the conversation grows, auto-collapse the item (smoothly) and keep scrolled to the latest.
  useEffect(() => {
    if (!activeChat) return;
    if (messages.length > prevCountRef.current) {
      setItemExpanded(false);
    }
    prevCountRef.current = messages.length;
    const el = scrollRef.current;
    if (!el) return;
    requestAnimationFrame(() => el.scrollTo({
      top: el.scrollHeight,
      behavior: 'smooth'
    }));
  }, [messages.length, activeChat]);
  const send = () => {
    const text = draft.trim();
    if (!text || !activeChat) return;
    const id = Date.now();
    const time = new Date().toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit'
    });
    const chatId = activeChat.id;
    setThreads(prev => ({
      ...prev,
      [chatId]: [...(prev[chatId] ?? []), {
        id,
        fromMe: true,
        text,
        time,
        status: 'sent'
      }]
    }));
    setDraft('');
    // Collapse the item banner once the conversation is active.
    setItemExpanded(false);
    // Talab AI scam detection — watch for risky money-transfer language.
    const scamPattern = /\b(transfer|send|wire|deposit|western union|moneygram|crypto|bitcoin|usdt|gift card|advance|upfront|otp|pin|bank details|iban)\b.*\b(money|cash|payment|fee|amount|funds|deposit|aed|usd)\b|\b(send|transfer|wire) (me|us|the) (money|cash|payment|deposit|funds)\b/i;
    if (scamPattern.test(text)) {
      window.setTimeout(() => {
        setThreads(prev => ({
          ...prev,
          [chatId]: [...(prev[chatId] ?? []), {
            id: id + 1,
            fromMe: false,
            kind: 'safety',
            text: 'Talab detected language about sending or transferring money. This is a common pattern in scams. Never transfer money or share OTPs, PINs, or bank details before verifying the seller and inspecting the item. Always use trusted, traceable payment methods.',
            time
          }]
        }));
      }, 500);
    }
    const update = (status: MsgStatus) => setThreads(prev => ({
      ...prev,
      [chatId]: (prev[chatId] ?? []).map(m => m.id === id ? {
        ...m,
        status
      } : m)
    }));
    window.setTimeout(() => update('delivered'), 700);
    window.setTimeout(() => update('read'), 1800);
  };
  return <div className="flex-1 flex flex-col h-full bg-white overflow-hidden relative" data-id="element-2692">
      <AnimatePresence mode="wait" initial={false} data-id="element-2693">
        {!activeChat /* ---------------- Inbox list ---------------- */ ? <motion.div key="inbox" initial={{
        opacity: 0,
        x: -16
      }} animate={{
        opacity: 1,
        x: 0
      }} exit={{
        opacity: 0,
        x: -16
      }} transition={{
        duration: 0.2
      }} className="flex-1 flex flex-col min-h-0" data-id="element-2694">
            <div className="flex-shrink-0 px-4 pt-5 pb-3 bg-white" data-id="element-2695">
              <h1 className="font-display font-bold text-2xl text-dark-navy mb-3" data-id="element-2696">
                Messages
              </h1>
              <div className="relative" data-id="element-2697">
                <SearchIcon className="w-[18px] h-[18px] text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" data-id="element-2698" />
                <input type="text" placeholder="Search messages…" className="w-full pl-11 pr-4 py-2.5 bg-slate-100 border border-transparent rounded-xl text-sm outline-none focus:bg-white focus:border-primary-indigo/40 transition-all" data-id="element-2699" />
              </div>
            </div>

            <div className="flex-1 overflow-y-auto" data-id="element-2700">
              {mockConversations.map(chat => <button key={chat.id} onClick={() => setActiveChat(chat)} className="w-full text-left px-4 py-3.5 flex gap-3 items-center active:bg-slate-50 transition-colors" data-id="element-2701">
                  <Avatar chat={chat} data-id="element-2702" />
                  <div className="flex-1 min-w-0 border-b border-slate-100 pb-3.5 -mb-3.5" data-id="element-2703">
                    <div className="flex justify-between items-baseline mb-0.5" data-id="element-2704">
                      <h3 className="font-semibold text-slate-900 truncate pr-2 flex items-center gap-1" data-id="element-2705">
                        <span className="truncate" data-id="element-2706">{chat.name}</span>
                        {chat.type === 'Verified Business' && <ShieldCheckIcon className="w-3.5 h-3.5 text-success flex-shrink-0" data-id="element-2707" />}
                      </h3>
                      <span className={`text-xs whitespace-nowrap ${chat.unread > 0 ? 'text-primary-indigo font-semibold' : 'text-slate-400'}`} data-id="element-2708">
                        {chat.time}
                      </span>
                    </div>
                    <div className="flex items-center gap-2" data-id="element-2709">
                      <p className={`text-sm truncate flex-1 ${chat.unread > 0 ? 'font-medium text-slate-700' : 'text-slate-500'}`} data-id="element-2710">
                        {chat.listing && <span className="text-slate-400" data-id="element-2711">
                            {chat.listing} ·{' '}
                          </span>}
                        {chat.lastMessage}
                      </p>
                      {chat.unread > 0 && <span className="min-w-[20px] h-5 px-1.5 bg-primary-indigo text-white text-[11px] font-bold flex items-center justify-center rounded-full flex-shrink-0" data-id="element-2712">
                          {chat.unread}
                        </span>}
                    </div>
                  </div>
                </button>)}
            </div>
          </motion.div> /* ---------------- Conversation thread ---------------- */ : <motion.div key="thread" initial={{
        opacity: 0,
        x: 24
      }} animate={{
        opacity: 1,
        x: 0
      }} exit={{
        opacity: 0,
        x: 24
      }} transition={{
        duration: 0.22
      }} className="absolute inset-0 flex flex-col bg-slate-50" data-id="element-2713">
            {/* Thread header */}
            <div className="flex-shrink-0 bg-white border-b border-slate-100 px-2 py-2.5 flex items-center gap-1" data-id="element-2714">
              <button onClick={() => setActiveChat(null)} aria-label="Back to messages" className="p-1.5 text-slate-600 active:bg-slate-100 rounded-full transition-colors flex-shrink-0" data-id="element-2715">
                <ChevronLeftIcon className="w-6 h-6" data-id="element-2716" />
              </button>
              <Avatar chat={activeChat} size="sm" data-id="element-2717" />
              <div className="flex-1 min-w-0 ml-1" data-id="element-2718">
                <div className="flex items-center gap-1" data-id="element-2719">
                  <h2 className="font-semibold text-slate-900 truncate" data-id="element-2720">
                    {activeChat.name}
                  </h2>
                  {activeChat.type === 'Verified Business' && <ShieldCheckIcon className="w-4 h-4 text-success flex-shrink-0" data-id="element-2721" />}
                </div>
                <span className={`block text-xs font-medium ${presence?.cls}`} data-id="element-2722">
                  {presence?.text}
                </span>
              </div>
              <button className="p-2 text-slate-500 active:bg-slate-100 rounded-full transition-colors flex-shrink-0" data-id="element-2723">
                <PhoneIcon className="w-[18px] h-[18px]" data-id="element-2724" />
              </button>
              <button className="p-2 text-slate-500 active:bg-slate-100 rounded-full transition-colors flex-shrink-0" data-id="element-2725">
                <MoreVerticalIcon className="w-5 h-5" data-id="element-2726" />
              </button>
            </div>

            {/* Item being discussed — fixed under the header, expandable/collapsible */}
            {activeChat.listing && <div className="flex-shrink-0 bg-white border-b border-slate-100 overflow-hidden" data-id="element-2727">
                {/* Collapsed bar — always present, acts as the toggle */}
                <button onClick={() => setItemExpanded(v => !v)} className="w-full flex items-center gap-2.5 px-3 py-2 text-left active:bg-slate-50 transition-colors" data-id="element-2728">
                  {activeChat.listingImage && <img src={activeChat.listingImage} alt="" className="w-7 h-7 rounded-lg object-cover flex-shrink-0 border border-slate-200" data-id="element-2729" />}
                  <span className="flex-1 min-w-0 text-[13px] font-medium text-slate-700 truncate" data-id="element-2730">
                    {activeChat.listing}
                    {activeChat.listingPrice && <span className="text-primary-indigo font-semibold" data-id="element-2731">
                        {' · '}
                        {activeChat.listingPrice}
                      </span>}
                  </span>
                  <motion.span animate={{
              rotate: itemExpanded ? 180 : 0
            }} transition={{
              duration: 0.2
            }} className="flex-shrink-0 text-slate-400" data-id="element-2732">
                    <ChevronDownIcon className="w-4 h-4" data-id="element-2733" />
                  </motion.span>
                </button>

                {/* Expanded detail */}
                <AnimatePresence initial={false} data-id="element-2734">
                  {itemExpanded && <motion.div key="item-detail" initial={{
              height: 0,
              opacity: 0
            }} animate={{
              height: 'auto',
              opacity: 1
            }} exit={{
              height: 0,
              opacity: 0
            }} transition={{
              duration: 0.25,
              ease: 'easeInOut'
            }} className="overflow-hidden" data-id="element-2735">
                      <div className="px-3 pb-3 flex items-center gap-3" data-id="element-2736">
                        {activeChat.listingImage && <img src={activeChat.listingImage} alt={activeChat.listing} className="w-14 h-14 rounded-xl object-cover flex-shrink-0 border border-slate-200" data-id="element-2737" />}
                        <div className="flex-1 min-w-0" data-id="element-2738">
                          <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-400" data-id="element-2739">
                            About this item
                          </span>
                          <h3 className="text-sm font-semibold text-dark-navy truncate leading-tight" data-id="element-2740">
                            {activeChat.listing}
                          </h3>
                          {activeChat.listingPrice && <span className="text-[13px] font-bold text-primary-indigo" data-id="element-2741">
                              {activeChat.listingPrice}
                            </span>}
                        </div>
                        <button onClick={() => setShowItemDetail(true)} className="px-3 py-1.5 text-xs font-semibold text-primary-indigo bg-primary-indigo/[0.07] rounded-full active:bg-primary-indigo/[0.12] transition-colors flex-shrink-0" data-id="element-2742">
                          View
                        </button>
                      </div>
                      <div className="px-3 pb-3 flex items-center gap-2 overflow-x-auto hide-scrollbar" data-id="element-2743">
                        {['More photos', 'Schedule viewing', 'Location'].map(a => <button key={a} className="px-2.5 py-1 bg-slate-50 border border-slate-200 rounded-full text-[11px] font-medium text-slate-600 active:bg-slate-100 whitespace-nowrap transition-colors flex-shrink-0" data-id="element-2744">
                              {a}
                            </button>)}
                      </div>
                    </motion.div>}
                </AnimatePresence>
              </div>}

            {/* Messages */}
            <div ref={scrollRef} className="flex-1 px-3 pt-4 pb-24 overflow-y-auto" data-id="element-2745">
              <div className="flex justify-center mb-4" data-id="element-2746">
                <span className="text-[11px] font-medium text-slate-500 bg-white border border-slate-200 px-2.5 py-1 rounded-full shadow-sm" data-id="element-2747">
                  Today
                </span>
              </div>
              <div className="space-y-1.5" data-id="element-2748">
                <AnimatePresence initial={false} data-id="element-2749">
                  {messages.map(m => <motion.div key={m.id} layout initial={{
                opacity: 0,
                y: 8,
                scale: 0.98
              }} animate={{
                opacity: 1,
                y: 0,
                scale: 1
              }} transition={{
                type: 'spring',
                stiffness: 420,
                damping: 30
              }} className={`flex ${m.kind === 'safety' ? 'justify-center' : m.fromMe ? 'justify-end' : 'justify-start'}`} data-id="element-2750">
                      {m.kind === 'safety' ? <div className="w-full max-w-[92%] bg-amber-50 border border-amber-200 rounded-2xl px-3.5 py-3 my-1" data-id="element-2751">
                          <div className="flex items-center gap-1.5 mb-1.5" data-id="element-2752">
                            <ShieldAlertIcon className="w-4 h-4 text-amber-600 flex-shrink-0" data-id="element-2753" />
                            <span className="text-[12px] font-bold text-amber-800" data-id="element-2754">
                              Talab Safety · Scam check
                            </span>
                          </div>
                          <p className="text-[13px] leading-relaxed text-amber-900/90" data-id="element-2755">
                            {m.text}
                          </p>
                          <div className="mt-2 flex items-center gap-1.5 text-[11px] text-amber-700/80" data-id="element-2756">
                            <SparklesIcon className="w-3 h-3" data-id="element-2757" />
                            Automated protection — only you can see this.
                          </div>
                        </div> : m.fromMe ? <div className="bg-primary-indigo text-white rounded-2xl rounded-br-md px-3 py-2 max-w-[78%] shadow-sm" data-id="element-2758">
                          <p className="text-[14px] leading-relaxed" data-id="element-2759">
                            {m.text}
                          </p>
                          <div className="flex items-center justify-end gap-1 mt-0.5 -mb-0.5" data-id="element-2760">
                            <span className="text-[10px] text-white/70" data-id="element-2761">
                              {m.time}
                            </span>
                            <Ticks status={m.status} data-id="element-2762" />
                          </div>
                        </div> : <div className="bg-white border border-slate-200 text-slate-800 rounded-2xl rounded-bl-md px-3 py-2 max-w-[78%] shadow-sm" data-id="element-2763">
                          <p className="text-[14px] leading-relaxed" data-id="element-2764">
                            {m.text}
                          </p>
                          <div className="flex justify-end mt-0.5 -mb-0.5" data-id="element-2765">
                            <span className="text-[10px] text-slate-400" data-id="element-2766">
                              {m.time}
                            </span>
                          </div>
                        </div>}
                    </motion.div>)}
                </AnimatePresence>
              </div>
            </div>

            {/* Floating input */}
            <div className="absolute inset-x-0 bottom-0 px-3 pb-6 pointer-events-none" data-id="element-2767">
              <div className="flex items-end gap-2 pointer-events-auto" data-id="element-2768">
                <div className="flex-1 flex items-center gap-1 bg-white rounded-full px-2 py-1 shadow-[0_8px_30px_rgb(0,0,0,0.12)] border border-slate-200 focus-within:ring-2 focus-within:ring-primary-indigo/15 focus-within:border-primary-indigo/30 transition-all" data-id="element-2769">
                  <button className="p-2 text-slate-400 active:text-primary-indigo rounded-full transition-colors flex-shrink-0" data-id="element-2770">
                    <PaperclipIcon className="w-5 h-5" data-id="element-2771" />
                  </button>
                  <input type="text" value={draft} onChange={e => setDraft(e.target.value)} onKeyDown={e => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  send();
                }
              }} placeholder="Message…" className="flex-1 bg-transparent outline-none text-sm text-slate-800 placeholder:text-slate-400 py-2" data-id="element-2772" />
                  <button aria-label="Record voice note" className="p-2 text-slate-400 active:text-primary-indigo rounded-full transition-colors flex-shrink-0" data-id="element-2773">
                    <MicIcon className="w-5 h-5" data-id="element-2774" />
                  </button>
                </div>
                <button onClick={send} disabled={!draft.trim()} aria-label="Send" className="w-11 h-11 flex items-center justify-center bg-primary-indigo text-white rounded-full active:scale-95 transition-transform flex-shrink-0 disabled:opacity-40 shadow-[0_8px_24px_rgb(67,56,202,0.4)]" data-id="element-2775">
                  <SendIcon className="w-5 h-5" data-id="element-2776" />
                </button>
              </div>
            </div>
          </motion.div>}
      </AnimatePresence>

      {listingDetail && <ListingDetailModal listing={listingDetail} open={showItemDetail} onClose={() => setShowItemDetail(false)} data-id="element-2777" />}
    </div>;
}